// ADR-0051 §3 (amended 2026-10-02) / V101-1-7: the provider registry.
// A reviewed code registry, like the connector manifests. One entry per
// third party a customer may depend on, plus the aliases that let
// discovery clues (an SDK in an SBOM, an OAuth app, a container image, a
// model id) resolve to it. The connector kinds that observe a provider are
// derived from the manifests (`connectorKindsForProvider`, G-1b); a new vendor
// is one entry here (its aliases are catalog data). Package aliases that need review-queue governance
// or per-workspace overrides live in ai_component_catalog (provider_id = key).
//
// Pure data — no runtime imports.
export const ACCESS_CHANNELS = [
    'direct', 'bedrock', 'vertex', 'azure_openai', 'gateway', 'self_hosted', 'ollama_cloud',
];
const sdk = (value, channel = 'direct', confidence = 0.9) => ({ kind: 'sdk', value, channel, confidence });
/** A container image. Model/data runtimes are self-hosted; a SaaS vendor's agent image means the SaaS is used (`direct`). */
const img = (value, confidence = 0.9, channel = 'self_hosted') => ({ kind: 'container_image', value, channel, confidence });
const dom = (value, confidence = 0.8) => ({ kind: 'domain', value, channel: 'direct', confidence });
const tf = (value, confidence = 0.9) => ({ kind: 'iac_provider', value, channel: 'direct', confidence });
const tag = (value, channel, confidence = 0.95) => ({ kind: 'model_tag', value, channel, confidence });
const bind = (value, confidence = 0.95) => ({ kind: 'wrangler_binding', value, channel: 'direct', confidence });
const res = (value, confidence = 0.95) => ({ kind: 'iac_resource', value, channel: 'direct', confidence });
const envv = (value, confidence = 0.85) => ({ kind: 'env_var', value, channel: 'direct', confidence });
const ci = (value, confidence = 0.9) => ({ kind: 'ci_config', value, channel: 'direct', confidence });
const crt = (value, confidence = 0.95) => ({ kind: 'cloud_resource_type', value, channel: 'direct', confidence });
const product = (id, name, ...aliases) => ({ id, name, aliases });
export const PROVIDERS = Object.freeze([
    // ── Model providers ───────────────────────────────────────────────────
    {
        key: 'anthropic', name: 'Anthropic', category: 'model_provider', vendorSlug: 'anthropic',
        channels: ['direct', 'bedrock', 'vertex', 'gateway'],
        aliases: [
            sdk('pypi:anthropic'), sdk('npm:@anthropic-ai/sdk'), sdk('golang:github.com/anthropics/anthropic-sdk-go'),
            sdk('npm:@anthropic-ai/bedrock-sdk', 'bedrock'), sdk('npm:@anthropic-ai/vertex-sdk', 'vertex'),
            sdk('npm:@ai-sdk/anthropic', 'direct', 0.85), sdk('pypi:langchain-anthropic', 'direct', 0.85),
            tag('anthropic.claude-', 'bedrock'), tag('us.anthropic.claude-', 'bedrock'), tag('eu.anthropic.claude-', 'bedrock'),
            tag('publishers/anthropic/', 'vertex'), tag('claude-', 'direct', 0.7),
            dom('claude.ai'), dom('anthropic.com'), envv('ANTHROPIC_API_KEY'),
            { kind: 'oauth_app', value: 'claude', channel: 'direct', confidence: 0.8 }, { kind: 'oauth_app', value: 'anthropic claude', channel: 'direct', confidence: 0.85 },
        ],
    },
    {
        key: 'openai', name: 'OpenAI', category: 'model_provider', vendorSlug: 'openai',
        channels: ['direct', 'azure_openai', 'gateway'],
        aliases: [
            // The openai SDK also talks to Azure OpenAI; direct is the default reading.
            sdk('pypi:openai', 'direct', 0.75), sdk('npm:openai', 'direct', 0.75),
            sdk('npm:@azure/openai', 'azure_openai'), sdk('pypi:langchain-openai', 'direct', 0.7), sdk('npm:@ai-sdk/openai', 'direct', 0.8),
            tag('gpt-', 'direct', 0.7), tag('o1-', 'direct', 0.6), tag('o3-', 'direct', 0.6),
            dom('chatgpt.com'), dom('chat.openai.com'), dom('openai.com'), envv('OPENAI_API_KEY'), { kind: 'oauth_app', value: 'chatgpt', channel: 'direct', confidence: 0.8 }, { kind: 'oauth_app', value: 'openai chatgpt', channel: 'direct', confidence: 0.85 },
        ],
    },
    {
        key: 'google-gemini', name: 'Google Gemini', category: 'model_provider', vendorSlug: 'google',
        channels: ['direct', 'vertex', 'gateway'],
        aliases: [
            sdk('pypi:google-generativeai'), sdk('pypi:google-genai'), sdk('npm:@google/generative-ai'), sdk('npm:@google/genai'),
            tag('publishers/google/models/gemini', 'vertex'), tag('gemini-', 'direct', 0.7), dom('gemini.google.com'),
        ],
    },
    {
        key: 'meta-llama', name: 'Meta Llama', category: 'model_provider',
        channels: ['bedrock', 'vertex', 'self_hosted', 'gateway'],
        aliases: [tag('meta.llama', 'bedrock'), tag('us.meta.llama', 'bedrock'), tag('publishers/meta/', 'vertex')],
    },
    {
        key: 'mistral', name: 'Mistral AI', category: 'model_provider', vendorSlug: 'mistral',
        channels: ['direct', 'bedrock', 'vertex', 'azure_openai', 'self_hosted'],
        aliases: [sdk('pypi:mistralai'), sdk('npm:@mistralai/mistralai'), tag('mistral.', 'bedrock'), dom('chat.mistral.ai')],
    },
    {
        key: 'cohere', name: 'Cohere', category: 'model_provider', vendorSlug: 'cohere',
        channels: ['direct', 'bedrock', 'azure_openai'],
        aliases: [sdk('pypi:cohere'), sdk('npm:cohere-ai'), tag('cohere.', 'bedrock')],
    },
    {
        key: 'amazon-nova', name: 'Amazon Nova / Titan', category: 'model_provider',
        channels: ['bedrock'],
        aliases: [tag('amazon.nova-', 'bedrock'), tag('us.amazon.nova-', 'bedrock'), tag('amazon.titan-', 'bedrock')],
    },
    {
        key: 'workers-ai', name: 'Cloudflare Workers AI', category: 'model_provider',
        channels: ['direct'],
        aliases: [tag('@cf/', 'direct')],
    },
    {
        key: 'huggingface', name: 'Hugging Face', category: 'ml_platform', vendorSlug: 'huggingface',
        channels: ['direct', 'self_hosted'],
        aliases: [sdk('pypi:huggingface-hub'), sdk('pypi:transformers', 'self_hosted', 0.6), sdk('npm:@huggingface/inference'), img('ghcr.io/huggingface/text-generation-inference')],
    },
    // ── Cloud / hosting channels ──────────────────────────────────────────
    {
        key: 'aws', name: 'Amazon Web Services', category: 'cloud', vendorSlug: 'aws',
        channels: ['direct', 'bedrock'],
        aliases: [tf('hashicorp/aws'), sdk('pypi:boto3', 'direct', 0.5), sdk('npm:@aws-sdk/client-sts', 'direct', 0.5)],
        // ADR-0075 §2: the products the V105-5-3 substitution catalog names.
        products: [
            product('rds-postgres', 'Amazon RDS for PostgreSQL', res('aws_db_instance', 0.6)),
            product('aurora-postgres', 'Amazon Aurora PostgreSQL', res('aws_rds_cluster', 0.6)),
            product('s3', 'Amazon S3', res('aws_s3_bucket'), crt('aws:s3:bucket')),
            product('ses', 'Amazon SES', res('aws_ses_domain_identity'), sdk('npm:@aws-sdk/client-ses')),
            product('sqs', 'Amazon SQS', res('aws_sqs_queue'), sdk('npm:@aws-sdk/client-sqs')),
            product('lambda', 'AWS Lambda', res('aws_lambda_function'), crt('aws:lambda:function')),
            product('bedrock', 'Amazon Bedrock'),
        ],
    },
    {
        key: 'gcp', name: 'Google Cloud', category: 'cloud', vendorSlug: 'gcp',
        channels: ['direct', 'vertex'],
        aliases: [tf('hashicorp/google')],
        products: [
            product('cloud-sql', 'Cloud SQL', res('google_sql_database_instance'), crt('gcp:sqladmin.googleapis.com/instance')),
            product('alloydb', 'AlloyDB', res('google_alloydb_cluster')),
            product('cloud-storage', 'Cloud Storage', res('google_storage_bucket'), crt('gcp:storage.googleapis.com/bucket')),
            product('vertex-ai', 'Vertex AI'),
        ],
    },
    {
        key: 'azure', name: 'Microsoft Azure', category: 'cloud', vendorSlug: 'azure',
        channels: ['direct', 'azure_openai'],
        aliases: [tf('hashicorp/azurerm')],
        products: [
            product('postgresql', 'Azure Database for PostgreSQL', res('azurerm_postgresql_flexible_server'), crt('azure:microsoft.dbforpostgresql/flexibleservers')),
            product('openai', 'Azure OpenAI', res('azurerm_cognitive_account', 0.5), crt('azure:microsoft.cognitiveservices/accounts', 0.5), envv('AZURE_OPENAI_API_KEY')),
            product('blob-storage', 'Azure Blob Storage', res('azurerm_storage_account', 0.6), crt('azure:microsoft.storage/storageaccounts', 0.6)),
        ],
    },
    {
        key: 'cloudflare', name: 'Cloudflare', category: 'platform', vendorSlug: 'cloudflare',
        channels: ['direct', 'gateway'],
        aliases: [tf('cloudflare/cloudflare'), sdk('npm:wrangler', 'direct', 0.6), sdk('npm:cloudflare', 'direct', 0.7), dom('cloudflare.com', 0.7)],
        // ADR-0075 §2: one entry per product whose coverage can differ.
        products: [
            // A wrangler config is itself a Worker; `services` / `assets` bindings are Worker features.
            product('workers', 'Workers', res('cloudflare_workers_script'), res('cloudflare_worker_script'), bind('services', 0.8), bind('assets', 0.8)),
            product('kv', 'Workers KV', bind('kv_namespaces'), res('cloudflare_workers_kv_namespace')),
            product('r2', 'R2', bind('r2_buckets'), res('cloudflare_r2_bucket')),
            product('d1', 'D1', bind('d1_databases'), res('cloudflare_d1_database')),
            product('durable-objects', 'Durable Objects', bind('durable_objects')),
            product('hyperdrive', 'Hyperdrive', bind('hyperdrive'), res('cloudflare_hyperdrive_config')),
            product('queues', 'Queues', bind('queues'), res('cloudflare_queue')),
            product('workflows', 'Workflows', bind('workflows')),
            product('vectorize', 'Vectorize', bind('vectorize')),
            product('workers-ai', 'Workers AI', bind('ai')),
            // AI Gateway has no binding: its URL lives in a variable. `ai.cloudflare.com` matches the gateway host by subdomain.
            product('ai-gateway', 'AI Gateway', dom('ai.cloudflare.com', 0.9), envv('CF_AIG_TOKEN'), envv('AI_GATEWAY_URL', 0.75), envv('ANTHROPIC_GATEWAY_URL', 0.75)),
            product('browser-rendering', 'Browser Rendering', bind('browser'), sdk('npm:@cloudflare/puppeteer'), envv('BROWSER_RENDERING_ACCOUNT_ID', 0.8), envv('BROWSER_RENDERING_TOKEN', 0.8)),
            product('pages', 'Pages', res('cloudflare_pages_project')),
        ],
    },
    // ── AI gateways / inference runtimes ──────────────────────────────────
    {
        key: 'portkey', name: 'Portkey', category: 'ai_gateway', channels: ['gateway'],
        aliases: [sdk('npm:portkey-ai', 'gateway'), sdk('pypi:portkey-ai', 'gateway')],
    },
    {
        key: 'litellm', name: 'LiteLLM', category: 'ai_gateway', channels: ['gateway', 'self_hosted'],
        aliases: [sdk('pypi:litellm', 'gateway'), img('ghcr.io/berriai/litellm')],
    },
    {
        key: 'openrouter', name: 'OpenRouter', category: 'ai_gateway', channels: ['gateway'],
        aliases: [{ kind: 'domain', value: 'openrouter.ai', channel: 'gateway', confidence: 0.8 }],
    },
    {
        key: 'ollama', name: 'Ollama', category: 'inference_runtime', vendorSlug: 'ollama',
        channels: ['self_hosted', 'ollama_cloud'],
        aliases: [img('ollama/ollama'), sdk('pypi:ollama', 'self_hosted', 0.85), sdk('npm:ollama', 'self_hosted', 0.85), tag(':cloud', 'ollama_cloud', 0.95)],
    },
    {
        key: 'vllm', name: 'vLLM', category: 'inference_runtime', channels: ['self_hosted'],
        aliases: [img('vllm/vllm-openai'), sdk('pypi:vllm', 'self_hosted', 0.85)],
    },
    {
        key: 'modal', name: 'Modal', category: 'inference_runtime', vendorSlug: 'modal', channels: ['direct'],
        aliases: [sdk('pypi:modal'), dom('modal.com')],
    },
    // ── Vector databases ──────────────────────────────────────────────────
    {
        key: 'pinecone', name: 'Pinecone', category: 'vector_db', vendorSlug: 'pinecone', channels: ['direct'],
        aliases: [sdk('pypi:pinecone-client'), sdk('pypi:pinecone'), sdk('npm:@pinecone-database/pinecone'), dom('pinecone.io')],
    },
    {
        key: 'qdrant', name: 'Qdrant', category: 'vector_db', vendorSlug: 'qdrant', channels: ['direct', 'self_hosted'],
        aliases: [img('qdrant/qdrant'), sdk('pypi:qdrant-client', 'self_hosted', 0.8), sdk('npm:@qdrant/js-client-rest', 'self_hosted', 0.8)],
    },
    {
        key: 'weaviate', name: 'Weaviate', category: 'vector_db', vendorSlug: 'weaviate', channels: ['direct', 'self_hosted'],
        aliases: [img('semitechnologies/weaviate'), img('cr.weaviate.io/semitechnologies/weaviate'), sdk('pypi:weaviate-client', 'self_hosted', 0.8)],
    },
    {
        key: 'milvus', name: 'Milvus', category: 'vector_db', channels: ['self_hosted', 'direct'],
        aliases: [img('milvusdb/milvus'), sdk('pypi:pymilvus', 'self_hosted', 0.8)],
    },
    {
        key: 'chroma', name: 'Chroma', category: 'vector_db', channels: ['self_hosted', 'direct'],
        aliases: [img('chromadb/chroma'), sdk('pypi:chromadb', 'self_hosted', 0.8)],
    },
    {
        key: 'pgvector', name: 'pgvector', category: 'vector_db', channels: ['self_hosted'],
        aliases: [img('pgvector/pgvector'), sdk('pypi:pgvector', 'self_hosted', 0.85), sdk('npm:pgvector', 'self_hosted', 0.85)],
    },
    // ── ML platforms ──────────────────────────────────────────────────────
    {
        key: 'mlflow', name: 'MLflow', category: 'ml_platform', vendorSlug: 'mlflow', channels: ['self_hosted', 'direct'],
        aliases: [sdk('pypi:mlflow', 'self_hosted', 0.8), img('ghcr.io/mlflow/mlflow')],
    },
    {
        key: 'wandb', name: 'Weights & Biases', category: 'ml_platform', vendorSlug: 'wandb', channels: ['direct'],
        aliases: [sdk('pypi:wandb'), sdk('npm:@wandb/sdk')],
    },
    {
        key: 'databricks', name: 'Databricks', category: 'ml_platform', vendorSlug: 'databricks', channels: ['direct'],
        aliases: [sdk('pypi:databricks-sdk'), tf('databricks/databricks')],
    },
    {
        key: 'microsoft-purview', name: 'Microsoft Purview', category: 'ml_platform', vendorSlug: 'purview', channels: ['direct'],
        aliases: [],
    },
    // ── Container platforms (V101-8-2) ────────────────────────────────────
    // A cluster is not an AI provider; it is where self-hosted AI runs. Listed so the
    // kubernetes connector kind has a provider_key (ADR-0051 §3). No aliases: workload
    // images resolve to the runtime they run (qdrant, ollama, vllm, ...), never to this.
    {
        key: 'kubernetes', name: 'Kubernetes', category: 'platform', vendorSlug: 'kubernetes',
        channels: ['self_hosted'], aliases: [],
    },
    // ── Data platforms ────────────────────────────────────────────────────
    {
        key: 'snowflake', name: 'Snowflake', category: 'platform', vendorSlug: 'snowflake', channels: ['direct'],
        aliases: [
            sdk('pypi:snowflake-connector-python'), sdk('pypi:snowflake-snowpark-python'), sdk('npm:snowflake-sdk'), sdk('golang:github.com/snowflakedb/gosnowflake'),
            tf('snowflakedb/snowflake'), tf('snowflake-labs/snowflake'), dom('snowflakecomputing.com'),
        ],
    },
    // ── Observability ─────────────────────────────────────────────────────
    {
        key: 'datadog', name: 'Datadog', category: 'observability', vendorSlug: 'datadog', channels: ['direct'],
        aliases: [sdk('npm:dd-trace'), sdk('pypi:ddtrace'), tf('datadog/datadog'), img('datadog/agent', 0.9, 'direct'), img('gcr.io/datadoghq/agent', 0.9, 'direct'), dom('datadoghq.com')],
    },
    {
        key: 'langsmith', name: 'LangSmith', category: 'observability', channels: ['direct'],
        aliases: [sdk('pypi:langsmith'), sdk('npm:langsmith')],
    },
    {
        key: 'langfuse', name: 'Langfuse', category: 'observability', channels: ['direct', 'self_hosted'],
        aliases: [sdk('pypi:langfuse'), sdk('npm:langfuse'), img('langfuse/langfuse')],
    },
    { key: 'grafana', name: 'Grafana', category: 'observability', vendorSlug: 'grafana', channels: ['direct', 'self_hosted'], aliases: [img('grafana/grafana'), tf('grafana/grafana'), dom('grafana.net'), dom('grafana.com')], products: [product('cloud', 'Grafana Cloud')] },
    { key: 'splunk', name: 'Splunk', category: 'observability', vendorSlug: 'splunk', channels: ['direct'], aliases: [] },
    { key: 'microsoft-sentinel', name: 'Microsoft Sentinel', category: 'observability', vendorSlug: 'sentinel', channels: ['direct'], aliases: [] },
    { key: 'newrelic', name: 'New Relic', category: 'observability', vendorSlug: 'newrelic', channels: ['direct'], aliases: [sdk('npm:newrelic'), sdk('pypi:newrelic')] },
    // ── Identity, SCM, security, collaboration ────────────────────────────
    { key: 'okta', name: 'Okta', category: 'idp', vendorSlug: 'okta', channels: ['direct'], aliases: [tf('okta/okta')] },
    { key: 'microsoft-entra', name: 'Microsoft Entra ID', category: 'idp', vendorSlug: 'entra', channels: ['direct'], aliases: [tf('hashicorp/azuread')] },
    { key: 'google-workspace', name: 'Google Workspace', category: 'idp', vendorSlug: 'google-workspace', channels: ['direct'], aliases: [dom('workspace.google.com')] },
    { key: 'microsoft-365', name: 'Microsoft 365', category: 'collaboration', vendorSlug: 'microsoft-365', channels: ['direct'], aliases: [] },
    { key: 'github', name: 'GitHub', category: 'scm', vendorSlug: 'github', channels: ['direct'], aliases: [tf('integrations/github'), dom('github.com'), ci('.github/workflows')] },
    { key: 'gitlab', name: 'GitLab', category: 'scm', vendorSlug: 'gitlab', channels: ['direct'], aliases: [tf('gitlabhq/gitlab'), ci('.gitlab-ci.yml')] },
    { key: 'bitbucket', name: 'Bitbucket', category: 'scm', vendorSlug: 'bitbucket', channels: ['direct'], aliases: [ci('bitbucket-pipelines.yml')] },
    { key: 'terraform-cloud', name: 'Terraform Cloud', category: 'platform', vendorSlug: 'terraform-cloud', channels: ['direct'], aliases: [tf('hashicorp/tfe')] },
    { key: 'tenable', name: 'Tenable', category: 'security', vendorSlug: 'tenable', channels: ['direct'], aliases: [] },
    { key: 'slack', name: 'Slack', category: 'collaboration', vendorSlug: 'slack', channels: ['direct'], aliases: [] },
    { key: 'notion', name: 'Notion', category: 'collaboration', vendorSlug: 'notion', channels: ['direct'], aliases: [] },
    { key: 'confluence', name: 'Confluence', category: 'collaboration', vendorSlug: 'confluence', channels: ['direct'], aliases: [] },
    // ── ITSM (V102-9-5) ───────────────────────────────────────────────────
    { key: 'jira', name: 'Jira', category: 'collaboration', vendorSlug: 'jira', channels: ['direct'], aliases: [] },
    { key: 'servicenow', name: 'ServiceNow', category: 'collaboration', vendorSlug: 'servicenow', channels: ['direct'], aliases: [] },
    // ── AI apps seen in shadow-AI discovery ───────────────────────────────
    { key: 'github-copilot', name: 'GitHub Copilot', category: 'ai_app', channels: ['direct'], aliases: [{ kind: 'oauth_app', value: 'github copilot', channel: 'direct', confidence: 0.9 }] },
    { key: 'otter', name: 'Otter.ai', category: 'ai_app', channels: ['direct'], aliases: [dom('otter.ai'), { kind: 'oauth_app', value: 'otter.ai', channel: 'direct', confidence: 0.85 }] },
    { key: 'gamma', name: 'Gamma', category: 'ai_app', channels: ['direct'], aliases: [dom('gamma.app')] },
    { key: 'deepl', name: 'DeepL', category: 'ai_app', channels: ['direct'], aliases: [dom('deepl.com')] },
    { key: 'gong', name: 'Gong', category: 'ai_app', vendorSlug: 'gong', channels: ['direct'], aliases: [dom('gong.io')] },
    // ── Infrastructure in a FedRAMP inventory (v10.5 / ADR-0072 §1, ADR-0075 §2) ──
    {
        key: 'neon', name: 'Neon', category: 'database', vendorSlug: 'neon', channels: ['direct'],
        aliases: [sdk('npm:@neondatabase/serverless'), dom('neon.tech'), dom('neon.com'), envv('NEON_DATABASE_URL')],
        products: [product('postgres', 'Neon Postgres')],
    },
    {
        key: 'workos', name: 'WorkOS', category: 'idp', vendorSlug: 'workos', channels: ['direct'],
        aliases: [sdk('npm:@workos-inc/node'), sdk('npm:@workos-inc/authkit-nextjs'), sdk('pypi:workos'), dom('workos.com'), envv('WORKOS_API_KEY'), envv('WORKOS_CLIENT_ID', 0.8)],
        products: [product('authkit', 'AuthKit and SSO')],
    },
    {
        key: 'resend', name: 'Resend', category: 'email', vendorSlug: 'resend', channels: ['direct'],
        aliases: [sdk('npm:resend'), sdk('pypi:resend'), dom('resend.com'), envv('RESEND_API_KEY')],
        products: [product('email', 'Transactional email')],
    },
    {
        key: 'stripe', name: 'Stripe', category: 'payments', vendorSlug: 'stripe', channels: ['direct'],
        aliases: [sdk('npm:stripe'), sdk('pypi:stripe'), dom('stripe.com'), envv('STRIPE_SECRET_KEY'), envv('STRIPE_WEBHOOK_SECRET', 0.8)],
        products: [product('billing', 'Billing and payments')],
    },
    {
        key: 'sigstore', name: 'Sigstore (public good)', category: 'security', channels: ['direct'],
        aliases: [sdk('npm:sigstore'), sdk('pypi:sigstore'), dom('sigstore.dev')],
        products: [
            product('fulcio', 'Fulcio', dom('fulcio.sigstore.dev', 0.9), envv('SIGSTORE_FULCIO_URL')),
            product('rekor', 'Rekor', dom('rekor.sigstore.dev', 0.9), envv('SIGSTORE_REKOR_URL')),
        ],
    },
    { key: 'twilio', name: 'Twilio', category: 'communications', vendorSlug: 'twilio', channels: ['direct'], aliases: [sdk('npm:twilio'), sdk('pypi:twilio'), dom('twilio.com')] },
    { key: 'sendgrid', name: 'SendGrid', category: 'email', vendorSlug: 'sendgrid', channels: ['direct'], aliases: [sdk('npm:@sendgrid/mail'), dom('sendgrid.com')] },
    { key: 'sentry', name: 'Sentry', category: 'observability', vendorSlug: 'sentry', channels: ['direct'], aliases: [sdk('npm:@sentry/node'), sdk('pypi:sentry-sdk'), dom('sentry.io'), envv('SENTRY_DSN')] },
    { key: '1password', name: '1Password', category: 'security', vendorSlug: '1password', channels: ['direct'], aliases: [dom('1password.com')] },
    // ── GAC Pro itself (ingest kinds) ─────────────────────────────────────
    { key: 'gacpro', name: 'GAC Pro', category: 'platform', channels: ['direct'], aliases: [] },
]);
/**
 * Channel-only aliases: the clue proves how a model is reached, not whose
 * model it is (D-CI-14). Resolution returns the channel with no provider.
 */
export const CHANNEL_ALIASES = Object.freeze([
    { kind: 'sdk', value: 'npm:@aws-sdk/client-bedrock-runtime', channel: 'bedrock', confidence: 0.9 },
    { kind: 'sdk', value: 'npm:@aws-sdk/client-bedrock-agent-runtime', channel: 'bedrock', confidence: 0.9 },
    { kind: 'sdk', value: 'pypi:langchain-aws', channel: 'bedrock', confidence: 0.7 },
    { kind: 'sdk', value: 'pypi:google-cloud-aiplatform', channel: 'vertex', confidence: 0.9 },
    { kind: 'sdk', value: 'npm:@google-cloud/vertexai', channel: 'vertex', confidence: 0.9 },
    { kind: 'sdk', value: 'npm:@ai-sdk/amazon-bedrock', channel: 'bedrock', confidence: 0.85 },
    { kind: 'sdk', value: 'npm:@ai-sdk/google-vertex', channel: 'vertex', confidence: 0.85 },
    { kind: 'sdk', value: 'pypi:azure-ai-inference', channel: 'azure_openai', confidence: 0.85 },
]);
const BY_KEY = new Map(PROVIDERS.map((p) => [p.key, p]));
export function getProvider(key) {
    return BY_KEY.get(key);
}
/**
 * Provider keys that are a product of another provider. ADR-0072 §1: `workers-ai` stays a
 * provider key (model clues resolve to it) and its inventory item is `cloudflare/workers-ai`.
 */
export const PRODUCT_KEY_ALIASES = Object.freeze({
    'workers-ai': { provider_key: 'cloudflare', product_id: 'workers-ai' },
});
export const formatProductRef = (r) => `${r.provider_key}/${r.product_id}`;
/** Parse `cloudflare/d1` (a bare provider key means `/*`). Unknown provider or product → null. */
export function parseProductRef(ref) {
    const t = ref.trim().toLowerCase();
    const alias = PRODUCT_KEY_ALIASES[t];
    if (alias)
        return alias;
    const [provider, productId = '*', ...rest] = t.split('/');
    if (rest.length || !provider)
        return null;
    const p = BY_KEY.get(provider);
    if (!p)
        return null;
    if (productId === '*')
        return { provider_key: p.key, product_id: '*' };
    return p.products?.some((x) => x.id === productId) ? { provider_key: p.key, product_id: productId } : null;
}
export function getProviderProduct(ref) {
    return BY_KEY.get(ref.provider_key)?.products?.find((x) => x.id === ref.product_id);
}
/** Every product-level alias with its provider and product. */
export function allProductAliases() {
    return PROVIDERS.flatMap((p) => (p.products ?? []).flatMap((x) => x.aliases.map((a) => ({ ...a, provider: p.key, product: x.id }))));
}
/**
 * Exact-match a clue to a product ref (no fuzzy matching, ADR-0075 §2). A product alias wins;
 * a provider-level alias resolves to `<provider>/*`; an ambiguous or unknown clue → null.
 */
export function resolveProductAlias(kind, value) {
    const v = value.trim().toLowerCase();
    const refs = new Set(allProductAliases().filter((a) => a.kind === kind && a.value.toLowerCase() === v).map((a) => `${a.provider}/${a.product}`));
    if (refs.size === 1)
        return parseProductRef([...refs][0]);
    if (refs.size > 1)
        return null;
    const prov = new Set(allAliases().filter((a) => a.kind === kind && a.provider && a.value.toLowerCase() === v).map((a) => a.provider));
    return prov.size === 1 ? { provider_key: [...prov][0], product_id: '*' } : null;
}
/** Every alias, flattened with its provider (channel-only aliases have none). */
export function allAliases() {
    return [
        ...PROVIDERS.flatMap((p) => p.aliases.map((a) => ({ ...a, provider: p.key }))),
        // Product clues also prove the provider (ADR-0075 §2).
        ...PROVIDERS.flatMap((p) => (p.products ?? []).flatMap((x) => x.aliases.map((a) => ({ ...a, provider: p.key })))),
        ...CHANNEL_ALIASES,
    ];
}
//# sourceMappingURL=providers.js.map